import { Breadcrumb, Button, Dropdown, Select, Spin, Tag } from "antd";
import React, { useState } from "react";
import Table, { ColumnType } from "antd/es/table";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { CloseOutlined, SlidersOutlined } from "@ant-design/icons";
import { DocumentFilterMenu } from "./documentFilter";
import MyPagination from "../../components/Pagination";
import { IDocumentPayloadType } from "../../../types/api/document-types";
import {
  useDocumentList,
  useDocumentStatusServiceUpdate,
} from "../../../hooks/api/document/hooks";
import { useMessageApi } from "../../../provider/AntdNotification";

const UploadedDocuments = () => {
  const [breadcrumb] = useState("documents");
  const messageApi = useMessageApi();
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const {
    data,
    refetchWithQuery: refetch,
    isFetching,
  } = useDocumentList({
    page: 1,
    limit: 20,
  });

  const { mutate: updateStatus } = useDocumentStatusServiceUpdate({
    onSuccess: () => {
      setUpdatingId(null);
      messageApi.success("Status updated successfully");
    },
    onError: (error: any) => {
      setUpdatingId(null);
      messageApi.error(error?.message);
    },
  });

  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string>
  >({});

  const removeFilter = (key: string) => {
    setSelectedFilters((prev) => {
      const updated = { ...prev };
      delete updated[key];
      refetch({ page: 1, limit: 20, ...updated });
      return updated;
    });
  };

  const qData = data?.qData ?? { page: 1, limit: 20 };

  /** ---- Status Dropdown Options ---- **/
  const STATUS_OPTIONS = [
    { label: "Pending", value: "PENDING" },
    { label: "Approved", value: "APPROVED" },
    { label: "Rejected", value: "REJECTED" },
    { label: "Require Correction", value: "REQUIRE_CORRECTION" },
  ];

  const STATUS_BADGES: Record<
    string,
    "processing" | "success" | "error" | "warning"
  > = {
    PENDING: "processing",
    APPROVED: "success",
    REJECTED: "error",
    REQUIRE_CORRECTION: "warning",
  };

  /** ---- Table Columns ---- **/
  const columns: ColumnType<IDocumentPayloadType>[] = [
    {
      title: "User Id",
      dataIndex: "user",
      render: (user) => user?.user_Id || "-",
    },
    {
      title: "Message",
      dataIndex: "message",
      render: (message) => message,
    },
    {
      title: "Uploaded On",
      dataIndex: "createdAt",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Document",
      dataIndex: "url",
      render: (url: string) =>
        url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            View / Download
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, row) => {
        return (
          <Select
            size="small"
            style={{ width: 160 }}
            value={row.status}
            disabled={updatingId === row._id}
            loading={updatingId === row._id}
            options={STATUS_OPTIONS}
            onChange={(value) => {
              setUpdatingId(row._id!);
              updateStatus({ _id: row._id, status: value });
            }}
          />
        );
      },
    },
    {
      title: "Preview",
      render: (_, row) => (
        <Tag color={STATUS_BADGES[row.status ?? ""]}>
          {row.status?.replace(/_/g, " ")}
        </Tag>
      ),
    },
  ];

  const DEFAULT_NUQS_CONFIG = { throttleMs: Infinity };

  return (
    <div>
      <h1 className="text-lg sm:text-3xl font-semibold">Uploaded Documents</h1>

      <Breadcrumb
        separator=">"
        items={[{ title: "Dashboard" }, { title: breadcrumb }]}
        className="text-xs sm:text-base my-2"
      />

      {/* Filter Button */}
      <div className="flex justify-end mb-3">
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          dropdownRender={() => (
            <DocumentFilterMenu
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              refetch={refetch}
            />
          )}
        >
          <Button
            icon={<SlidersOutlined />}
            className="bg-[#FEF2E7] custom_shadow"
          >
            Filters
          </Button>
        </Dropdown>
      </div>

      {/* Active Filters */}
      {Object.keys(selectedFilters).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(selectedFilters).map(([key, value]) => (
            <Tag
              key={key}
              closable
              color="green"
              onClose={() => removeFilter(key)}
              closeIcon={<CloseOutlined />}
            >
              {key}: {value}
            </Tag>
          ))}
        </div>
      )}

      {/* Table */}
      <Table
        dataSource={data?.data ?? []}
        columns={columns}
        rowKey={(item) => item._id ?? ""}
        loading={isFetching}
        pagination={false}
        scroll={{ x: true }}
        size="middle"
        className="custom-rounded-table"
        footer={() => (
          <MyPagination
            qData={qData}
            onChange={refetch}
            total={qData.total}
            DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
          />
        )}
      />
    </div>
  );
};

export default UploadedDocuments;
